<template>
  <q-page class="verdict-demo">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-primary="t('landing.nav.ctaPrimary')"
      :cta-secondary="t('landing.nav.ctaSecondary')"
      @cta-primary-click="goAuth"
      @cta-secondary-click="goSignIn"
    />

    <div class="verdict-demo__inner">
      <!-- Hero -->
      <header class="verdict-demo__hero">
        <h1 class="verdict-demo__title">{{ t('verdictDemo.title') }}</h1>
        <p class="verdict-demo__meta">
          <KdsIcon name="list-checks" :size="16" aria-hidden="true" />
          {{ t('verdictDemo.meta') }}
        </p>
      </header>

      <!-- Verdict block -->
      <KdsVerdictBlock
        :eyebrow="t('verdictDemo.eyebrow')"
        :quote="t('verdictDemo.quote')"
        :meta="t('verdictDemo.meta')"
        :rows="verdictRows"
      />

      <!-- CTA -->
      <div class="verdict-demo__cta-wrapper">
        <KdsButton variant="primary" size="md" @click="goAuth">
          {{ t('verdictDemo.ctaPrimary') }}
        </KdsButton>
        <p class="verdict-demo__cta-subtext">{{ t('verdictDemo.ctaSubtext') }}</p>
      </div>
    </div>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsButton from '@/kds/primitives/KdsButton.vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import KdsNav from '@/kds/components/KdsNav.vue';
import KdsVerdictBlock from '@/kds/components/KdsVerdictBlock.vue';
import { useSeoMeta } from '@/composables/useSeoMeta';
import { useConsent } from '@/composables/useConsent';
import { VERDICT_DEMO_DATA } from './VerdictDemoPage.demo';

const { t } = useI18n();
const router = useRouter();
const { openPreferences } = useConsent();

useSeoMeta('verdict-demo');

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '/#how' },
  { label: t('landing.nav.protocols'), href: '/#protocols' },
  { label: t('landing.nav.pricing'), href: '/#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const verdictRows = computed(() => VERDICT_DEMO_DATA.rows);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '/#how' },
      { label: t('landing.footer.linkProtocols'), href: '/#protocols' },
      { label: t('landing.footer.linkPricing'), href: '/#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      { label: t('landing.footer.linkContact'), to: { path: '/about', hash: '#contact' } }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

const goAuth = (): void => {
  void router.push({ name: 'auth', query: { mode: 'sign-up' } });
};

const goSignIn = (): void => {
  void router.push({ name: 'auth', query: { mode: 'sign-in' } });
};
</script>

<style scoped lang="scss">
.verdict-demo {
  background: var(--paper);
  color: var(--ink);

  &__inner {
    max-width: 880px;
    margin: 0 auto;
    padding: var(--space-11) var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-9);

    @media (min-width: 960px) {
      padding: var(--space-11) var(--space-7);
    }
  }

  &__hero {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    animation: verdict-settle var(--d-slow) var(--ease-settle) both;
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-display-m);
    line-height: 1.08;
    letter-spacing: -0.02em;
    color: var(--ink);

    @media (min-width: 960px) {
      font-size: var(--fs-display-l);
      line-height: 1.06;
      letter-spacing: -0.022em;
    }
  }

  &__meta {
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone);
    letter-spacing: 0.01em;
  }

  &__cta-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-3);
    padding-top: var(--space-5);
    border-top: var(--border);
    animation: verdict-settle var(--d-slow) var(--ease-settle) 100ms both;
  }

  &__cta-subtext {
    margin: 0;
    font-size: var(--fs-body-sm);
    color: var(--stone);
  }
}

@keyframes verdict-settle {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
